import type { PaymentKitEnvironment, PaymentKitErrors, PaymentKitStates, TInternalFuncs } from "../types";
import {
  definePaymentMethod,
  type FraudMetadataWire,
  getOrCreateCheckoutRequestId,
  toFraudMetadataWire,
} from "../utils";
import {
  type AddressPayload,
  clean,
  type ShippingFields,
  shippingAddressFromFields,
} from "../utils/wallet-billing-address";
import {
  AirwallexGooglePayAdapter,
  type AirwallexGooglePayConfig,
  AirwallexGooglePayMockScenario,
  type GooglePayEncryptedToken,
} from "./airwallex-google-pay-adapter";
import { type BeforeConfirmOptions, runBeforeConfirm } from "./before-confirm";
import { handleNextAction } from "./next-action-handlers";
import { GooglePayMockScenario, StripeGooglePayAdapter } from "./stripe-google-pay-adapter";
import {
  createWalletSessionConfigReader,
  fetchWalletProcessors,
  resolveWalletProcessorType,
  type WalletProcessorInfo,
} from "./wallet-processors";
import { type CheckoutTrialInfo, walletTotalLabel } from "./wallet-trial";

// Google Pay-specific types
export type GooglePayCustomerInfo = {
  first_name: string;
  last_name: string;
  email?: string;
  // A shipping address the shopper typed (the `shipping_*` submit fields). Wins over the sheet's.
  shipping_address?: AddressPayload;
};

export type GooglePayStartRequest = {
  processor_id: string;
  // Airwallex-only fields. The Stripe /google-pay/start is now a pure read and
  // ignores these — customer_info/fraud_metadata are sent to /google-pay/confirm
  // instead, where the customer + checkout attempt + SetupIntent are created.
  customer_info?: GooglePayCustomerInfo;
  fraud_metadata?: FraudMetadataWire;
  mock_scenario?: string;
};

export type GooglePayStartResponse = {
  /** Set by the legacy write-based /start; absent/null on the pure-read start (nothing written). */
  checkout_attempt_id?: string | null;
  amount: number;
  amount_display: string; // Formatted for Google Pay (e.g., "100.00" for USD, "1000" for JPY)
  currency: string;
  country: string;

  // Checkout mode (PAY-1732): "setup" vaults the wallet payment method without
  // charging; "payment" charges. Read this instead of inferring setup mode from
  // amount === 0 — trials and fully discounted baskets are also $0 but are NOT
  // setup mode (PAY-1511). Absent on older backends, which were payment-only.
  mode?: "setup" | "payment";

  // Free trial (PAY-2341, Stripe only): the total is $0 today, so the sheet's
  // total line says what the trial is and what it costs afterwards.
  trial?: CheckoutTrialInfo | null;

  // Processor discriminator
  processor: "stripe" | "airwallex";

  // Environment flag
  is_sandbox?: boolean;

  // Stripe-specific fields (when processor="stripe")
  client_secret?: string;
  stripe_pk?: string;

  // Airwallex-specific fields (when processor="airwallex")
  merchant_name?: string;
  airwallex_account_id?: string;
  google_merchant_id?: string; // Google-assigned merchant ID for production (BCR2DN...)
};

export type GooglePayConfirmRequest = {
  google_pay_token?: GooglePayEncryptedToken; // Required for Airwallex
  payer_email?: string; // Email from payment sheet (Stripe Google Pay)
  payer_billing_address?: AddressPayload; // Billing address from the Stripe sheet (PAY-1953)
  payer_shipping_address?: AddressPayload; // Shipping address from the sheet, when asked for
  mock_scenario?: string;
  // Create-everything confirm (Stripe & Airwallex): moved off /start (which is now a
  // pure read). At confirm time the backend creates the customer + checkout attempt
  // (+ SetupIntent for Stripe) and runs the charge.
  processor_id?: string;
  customer_info?: GooglePayCustomerInfo;
  fraud_metadata?: FraudMetadataWire;
  payment_method_id?: string;
  // Amount the prepared sheet displayed — the backend rejects the confirm when its
  // resolved amount differs (guards against charging more/less than authorized).
  expected_amount?: number;
  expected_currency?: string;
};

// Airwallex 3DS next action type (matches backend snake_case response)
export type Airwallex3dsNextAction = {
  type: "airwallex_3ds";
  url: string;
  method: string;
  payment_intent_id: string;
};

export type GooglePayConfirmResponse = {
  charge_status: "success" | "fail" | "pending";
  transaction_id?: string;
  error_code?: string;
  error_message?: string;
  error_message_for_customer?: string;
  error_message_for_debug?: string;
  /** Absent/null on failures that happened before an attempt existed. */
  checkout_attempt_id?: string | null;
  checkout_session_id?: string;
  next_action?: Airwallex3dsNextAction; // Present when charge_status="pending" for 3DS
  payment_intent_id?: string;
  customer_id?: string;
  /** Customer name on file after confirm (best-effort; may be empty). */
  customer_first_name?: string;
  customer_last_name?: string;
  payment_method_id?: string;
  processor_used?: string;
  subscription_id?: string;
  invoice_id?: string;
  invoice_number?: number;
  card_brand?: string;
  card_last4?: string;
  card_exp_month?: number;
  card_exp_year?: number;
};

export type GooglePaySubmitOptions = BeforeConfirmOptions & {
  /** Processor external ID — optional when using auto-prepare (SDK fetches from checkout session) */
  processorId?: string;
  customerInfo: GooglePayCustomerInfo;
  mockScenario?: GooglePayMockScenario;
};

type GooglePayResult =
  | { data: { [key: string]: unknown }; errors?: never }
  | { data?: never; errors: PaymentKitErrors };

// =============================================================================
// Helper Functions
// =============================================================================

async function apiCall<T>(
  url: string,
  options: RequestInit,
  checkoutRequestId?: string,
): Promise<{ data?: T; error?: string }> {
  // Add x-request-id header if provided
  const headers = new Headers(options.headers);
  if (checkoutRequestId) {
    headers.set("x-request-id", checkoutRequestId);
  }

  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    let errorMessage = `Request failed (${response.status})`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    return { error: errorMessage };
  }
  return { data: await response.json() };
}

function validateOptions(
  options: GooglePaySubmitOptions,
  autoPrepareConfig: AutoPrepareConfig | null,
): PaymentKitErrors | null {
  // processorId is optional when autoPrepareConfig provides it
  if (!options?.processorId && !autoPrepareConfig?.processorId) {
    return { processor_id: "Processor ID is required" };
  }
  // Customer name is optional — Google Pay collects it from the payment sheet
  return null;
}

// =============================================================================
// Auto-Prepare Types
// =============================================================================

type CheckoutSessionConfig = {
  express_checkout_processor_id?: string | null;
  express_checkout_processor_type?: string | null;
  // Google Pay processor resolved independently of the Apple Pay express
  // processor, so Google Pay readiness works when Apple Pay is disabled.
  google_pay_processor_id?: string | null;
  google_pay_processor_type?: string | null;
};

type AutoPrepareConfig = {
  processorId: string;
  processorType?: "stripe" | "airwallex";
};

// =============================================================================
// Prepared Payment State (pre-fetch before click — mirrors apple-pay.ts)
// =============================================================================

// The Stripe rail (W3C PaymentRequest) must be created + probed with canMakePayment()
// at prepare time so readiness reflects the SAME rail the charge runs on. We cache the
// prepared adapter so the click handler can show the sheet immediately (Stripe requires
// the sheet open within the user gesture). The W3C PaymentRequest is one-shot
// (show() throws on reuse), so this state must be cleared after every sheet showing.
type PreparedStripeGooglePayState = {
  kind: "stripe";
  adapter: StripeGooglePayAdapter;
  processorId: string;
  startData: GooglePayStartResponse;
  mockScenarioStr?: string;
  checkoutRequestId: string;
};

// Airwallex analog (PAY-1538): /google-pay/start is a pure read for Airwallex too, so
// the sheet config + initialized adapter are cached at prepare time and the click
// handler calls loadPaymentData() immediately. Chrome only keeps the tap's transient
// user activation alive ~5s — the old post-tap /start round-trip blew through it on
// slow networks and Chrome rejected the sheet popup with NotAllowedError.
// Unlike the W3C PaymentRequest, loadPaymentData() is reusable, so this state
// survives sheet cancels/errors — clearing it there would push the common
// cancel-then-retry tap back onto the gesture-unsafe legacy path.
// amount/currency are echoed to /confirm so the backend can reject a charge whose
// resolved amount no longer matches what the sheet displayed.
type PreparedAirwallexGooglePayState = {
  kind: "airwallex";
  adapter: AirwallexGooglePayAdapter;
  processorId: string;
  amount: number;
  currency: string;
  checkoutRequestId: string;
};

// Held per PaymentKit instance (inside definePaymentMethod's closure) — module-level
// globals here would let two instances on one page clobber each other's prepared sheet.
export type PreparedGooglePayState = PreparedStripeGooglePayState | PreparedAirwallexGooglePayState;

/**
 * Check if Google Pay is available on this device/browser.
 * Uses the Google Pay API's isReadyToPay() method.
 */
async function checkGooglePayAvailability(): Promise<boolean> {
  // Check if Google Pay API is loaded
  if (!window.google?.payments?.api?.PaymentsClient) {
    console.log("[GooglePay] Google Pay API not loaded");
    return false;
  }

  try {
    // Per Google Pay docs, TEST mode is appropriate for isReadyToPay() availability checks.
    // The actual payment flow uses the production environment configured in submit().
    const client = new window.google.payments.api.PaymentsClient({
      environment: "TEST",
    });

    const request = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: "CARD" as const,
          parameters: {
            allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"] as ("PAN_ONLY" | "CRYPTOGRAM_3DS")[],
            allowedCardNetworks: ["VISA", "MASTERCARD", "AMEX", "DISCOVER", "JCB"] as (
              | "VISA"
              | "MASTERCARD"
              | "AMEX"
              | "DISCOVER"
              | "JCB"
            )[],
          },
        },
      ],
    };

    const response = await client.isReadyToPay(request);
    return response.result;
  } catch (error) {
    console.error("[GooglePay] availability check error:", error);
    return false;
  }
}

function getMockScenarioStr(mockScenario?: GooglePayMockScenario): string | undefined {
  return mockScenario && mockScenario !== GooglePayMockScenario.None ? mockScenario : undefined;
}

async function callStartEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  options: GooglePaySubmitOptions,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
): Promise<{ data?: GooglePayStartResponse; error?: string }> {
  return apiCall<GooglePayStartResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/google-pay/start`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        processor_id: options.processorId,
        customer_info: options.customerInfo,
        fraud_metadata: toFraudMetadataWire(checkoutRequestId),
        mock_scenario: mockScenarioStr,
      } as GooglePayStartRequest),
    },
    checkoutRequestId,
  );
}

// =============================================================================
// Stripe Flow
// =============================================================================

function initializeStripeAdapter(
  stripePk: string,
  startData: GooglePayStartResponse,
  mockScenario?: GooglePayMockScenario,
  requestShipping?: boolean,
): { adapter?: StripeGooglePayAdapter; error?: string } {
  const adapter = new StripeGooglePayAdapter(mockScenario);

  if (!adapter.initialize(stripePk)) {
    return { error: 'Stripe.js not loaded. Add <script src="https://js.stripe.com/v3/"></script> to your page.' };
  }

  const prConfig = {
    country: startData.country,
    currency: startData.currency.toLowerCase(),
    total: {
      label: walletTotalLabel(startData.trial),
      amount: startData.amount,
      // Setup mode has no charge, so the total is $0 — which the wallet sheet
      // rejects unless it is marked pending.
      ...(startData.mode === "setup" ? { pending: true } : {}),
    },
    requestPayerName: true,
    requestPayerEmail: true,
    requestShipping,
  };
  adapter.createPaymentRequest(prConfig);
  return { adapter };
}

/**
 * Prepare the Stripe Google Pay rail BEFORE the click (mirrors prepareApplePay).
 *
 * Calls the pure-read /google-pay/start (stripe_pk + amount/currency/country — NO
 * SetupIntent, NO writes), initializes the Stripe adapter, creates the W3C
 * PaymentRequest, and probes canMakePayment(). The returned `.googlePay` is the SAME
 * signal the charge runs on, so readiness computed from it is correct on Safari
 * (where googlePay is false even though isReadyToPay() returns true — bug #3274).
 *
 * On success the prepared adapter is cached so the click handler can show the sheet
 * immediately within the user gesture.
 */
/**
 * Why Google Pay is not ready, surfaced through `onGooglePayReady(isReady, detail)`.
 * `rail_unavailable` covers both "browser doesn't support the charge rail" and "wallet
 * has no usable card" — the W3C PaymentRequest API deliberately returns a bare boolean
 * (privacy), so the SDK cannot distinguish those two.
 */
export type GooglePayUnreadyReason =
  | "preparing"
  | "session_fetch_failed"
  | "processors_fetch_failed"
  | "no_processor"
  | "start_failed"
  | "adapter_init_failed"
  | "rail_unavailable"
  | "prepare_error";

export type GooglePayReadyDetail = { reason: GooglePayUnreadyReason };

export async function prepareGooglePay(
  apiBaseUrl: string,
  secureToken: string,
  config: {
    processorId: string;
    processorType?: "stripe" | "airwallex";
    // Stripe only: its sheet is built here. Airwallex asks at tap time.
    requestShippingAddress?: boolean;
  },
  environment: PaymentKitEnvironment,
  mockScenario?: GooglePayMockScenario,
): Promise<{
  success: boolean;
  error?: string;
  googlePay?: boolean;
  reason?: GooglePayUnreadyReason;
  /** Present on success for typed processors — the caller owns storing/clearing it. */
  prepared?: PreparedGooglePayState;
}> {
  const mockScenarioStr = getMockScenarioStr(mockScenario);
  const checkoutRequestId = getOrCreateCheckoutRequestId(environment);

  // An unresolved processorType (undefined) stays on isReadyToPay() alone: the typed
  // branches below need to KNOW the processor to prepare its rail, and an unknown type
  // must not risk side effects on backends where /start still writes.
  if (config.processorType !== "stripe" && config.processorType !== "airwallex") {
    const available = await checkGooglePayAvailability();
    return { success: available, googlePay: available, reason: available ? undefined : "rail_unavailable" };
  }

  // Airwallex (PAY-1538): pure-read /start (sheet config only — the checkout attempt is
  // created at /confirm), then initialize the Google Pay adapter and probe isReadyToPay()
  // on it — that IS the Airwallex charge rail (it must NOT consult the Stripe
  // PaymentRequest rail; bug #3274 is Stripe-only). The prepared adapter lets the click
  // handler call loadPaymentData() immediately, within the tap's transient activation.
  if (config.processorType === "airwallex") {
    // Cheap presence check BEFORE the /start round-trip: wallet-less visitors
    // (e.g. Safari without pay.js) should not cost the backend a sheet-config
    // resolution on every page load that can never be used.
    if (!mockScenarioStr && !window.google?.payments?.api?.PaymentsClient) {
      console.log("[GooglePay] Google Pay API not loaded");
      return { success: false, googlePay: false, reason: "rail_unavailable" };
    }
    const startResult = await apiCall<GooglePayStartResponse>(
      `${apiBaseUrl}/api/checkout/${secureToken}/google-pay/start`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // No customer_info/fraud_metadata — their absence selects the backend's
        // pure-read branch. They are sent at /confirm instead.
        body: JSON.stringify({
          processor_id: config.processorId,
          mock_scenario: mockScenarioStr,
        } as GooglePayStartRequest),
      },
      checkoutRequestId,
    );

    if (startResult.error || !startResult.data) {
      // Backends that predate the pure-read Airwallex /start reject it (400 on ones
      // with the customer_info guard, 500 on older ones that dereference the missing
      // fields). Fall back to readiness-only so the button still works via the legacy
      // post-tap-/start submit path. Deploy ordering: backend before SDK publish.
      console.warn("[GooglePay] Airwallex prepare start failed, falling back to legacy flow:", startResult.error);
      const available = await checkGooglePayAvailability();
      return { success: available, googlePay: available, reason: available ? undefined : "rail_unavailable" };
    }

    const { adapter, error: adapterError } = initializeAirwallexAdapter(startResult.data, mockScenario);
    if (!adapter) {
      return { success: false, error: adapterError, reason: "adapter_init_failed" };
    }

    const available = await adapter.canMakePayment();
    if (!available) {
      return { success: false, googlePay: false, reason: "rail_unavailable" };
    }

    return {
      success: true,
      googlePay: true,
      prepared: {
        kind: "airwallex",
        adapter,
        processorId: config.processorId,
        amount: startResult.data.amount,
        currency: startResult.data.currency,
        checkoutRequestId,
      },
    };
  }

  // Stripe: pure-read /start → adapter → canMakePayment() on the W3C PaymentRequest rail.
  const startResult = await apiCall<GooglePayStartResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/google-pay/start`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        processor_id: config.processorId,
        mock_scenario: mockScenarioStr,
      } as GooglePayStartRequest),
    },
    checkoutRequestId,
  );
  if (startResult.error || !startResult.data) {
    return { success: false, error: startResult.error || "Failed to start Google Pay", reason: "start_failed" };
  }

  const startData = startResult.data;
  if (!startData.stripe_pk) {
    return { success: false, error: "Stripe publishable key not provided", reason: "start_failed" };
  }

  const { adapter, error: adapterError } = initializeStripeAdapter(
    startData.stripe_pk,
    startData,
    mockScenario,
    config.requestShippingAddress,
  );
  if (!adapter) {
    return { success: false, error: adapterError, reason: "adapter_init_failed" };
  }

  // The rail that decides readiness: false on Safari, true on Chrome.
  const googlePay = await adapter.canMakePayment();
  if (!googlePay) {
    return { success: false, googlePay: false, reason: "rail_unavailable" };
  }

  return {
    success: true,
    googlePay: true,
    prepared: {
      kind: "stripe",
      adapter,
      processorId: config.processorId,
      startData,
      mockScenarioStr,
      checkoutRequestId,
    },
  };
}

/**
 * Show the prepared Stripe Google Pay sheet and return the PaymentMethod id.
 * The SetupIntent + PaymentIntent are created server-side at /confirm.
 */
async function runPreparedStripeFlow(prepared: PreparedStripeGooglePayState): Promise<{
  success: boolean;
  error?: string;
  payerEmail?: string;
  paymentMethodId?: string;
  payerBillingAddress?: AddressPayload;
  payerShippingAddress?: AddressPayload;
}> {
  const paymentResult = await prepared.adapter.showPaymentSheet();

  if (!paymentResult.success) {
    if ("cancelled" in paymentResult && paymentResult.cancelled) {
      return { success: false, error: "Google Pay cancelled by user" };
    }
    const errorMessage = "error" in paymentResult ? paymentResult.error : "Unknown error";
    return { success: false, error: errorMessage };
  }

  // Complete the sheet UI now — the backend creates + confirms the SetupIntent from the
  // PaymentMethod id, so there is no client-side confirmCardSetup step to await anymore.
  paymentResult.complete("success");
  return {
    success: true,
    payerEmail: paymentResult.payerEmail,
    paymentMethodId: paymentResult.paymentMethodId,
    payerBillingAddress: paymentResult.payerBillingAddress,
    payerShippingAddress: paymentResult.payerShippingAddress,
  };
}

// =============================================================================
// Airwallex Flow
// =============================================================================

function initializeAirwallexAdapter(
  startData: GooglePayStartResponse,
  mockScenario?: GooglePayMockScenario,
): {
  adapter?: AirwallexGooglePayAdapter;
  error?: string;
} {
  if (!startData.merchant_name || !startData.airwallex_account_id) {
    return { error: "Airwallex Google Pay credentials not provided" };
  }

  // Convert GooglePayMockScenario to AirwallexGooglePayMockScenario
  let airwallexMockScenario: AirwallexGooglePayMockScenario = AirwallexGooglePayMockScenario.None;
  if (mockScenario === GooglePayMockScenario.Success) {
    airwallexMockScenario = AirwallexGooglePayMockScenario.Success;
  } else if (mockScenario === GooglePayMockScenario.Cancelled) {
    airwallexMockScenario = AirwallexGooglePayMockScenario.Cancelled;
  }

  const adapter = new AirwallexGooglePayAdapter(airwallexMockScenario);

  const config: AirwallexGooglePayConfig = {
    merchantId: startData.merchant_name,
    gatewayMerchantId: startData.airwallex_account_id,
    amountDisplay: startData.amount_display,
    currency: startData.currency,
    country: startData.country,
    isProduction: startData.is_sandbox === false,
    googleMerchantId: startData.google_merchant_id,
  };

  if (!adapter.initialize(config)) {
    return {
      error:
        'Google Pay API not loaded. Add <script src="https://pay.google.com/gp/p/js/pay.js"></script> to your page.',
    };
  }

  return { adapter };
}

async function runAirwallexFlow(
  startData: GooglePayStartResponse,
  mockScenario?: GooglePayMockScenario,
): Promise<{ success: boolean; token?: GooglePayEncryptedToken; payerEmail?: string; error?: string }> {
  // Initialize Airwallex adapter
  const { adapter, error: adapterError } = initializeAirwallexAdapter(startData, mockScenario);
  if (!adapter) {
    return { success: false, error: adapterError };
  }

  // Check availability
  const isAvailable = await adapter.canMakePayment();

  if (!isAvailable) {
    return { success: false, error: "Google Pay not available on this device" };
  }

  // Show payment sheet. Never asks for a shipping address: this legacy path's
  // /start already created the customer, and its /confirm takes no sheet address.
  // A typed shipping address still reaches the customer through /start.
  const paymentResult = await adapter.showPaymentSheet();

  if (!paymentResult.success) {
    if ("cancelled" in paymentResult && paymentResult.cancelled) {
      return { success: false, error: "Google Pay cancelled by user" };
    }
    const errorMessage = "error" in paymentResult ? paymentResult.error : "Unknown error";
    return { success: false, error: errorMessage };
  }

  // Return the token and payer email for passing to backend
  return { success: true, token: paymentResult.token, payerEmail: paymentResult.payerEmail };
}

// =============================================================================
// Confirm & Verify Endpoints
// =============================================================================

/**
 * Call /confirm endpoint - processes Google Pay token (Airwallex) or records setup (Stripe).
 * For Airwallex, may return next_action if 3DS is required.
 */
async function callConfirmEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  googlePayToken?: GooglePayEncryptedToken,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  payerEmail?: string,
  // Create-everything fields moved off the now pure-read /start: the customer +
  // checkout attempt (+ SetupIntent for Stripe) are created here. Stripe adds the
  // PaymentMethod id from Stripe.js's PaymentRequest; Airwallex (PAY-1538) instead
  // carries the google_pay_token above.
  processorConfirm?: {
    processorId?: string;
    customerInfo?: GooglePayCustomerInfo;
    paymentMethodId?: string;
    expectedAmount?: number;
    expectedCurrency?: string;
    // Billing address from the Stripe sheet (PAY-1953) — saved on the customer.
    payerBillingAddress?: AddressPayload;
    // Shipping address from the sheet, when asked for — saved on the customer.
    payerShippingAddress?: AddressPayload;
  },
): Promise<GooglePayConfirmResponse> {
  const requestBody: GooglePayConfirmRequest = {
    mock_scenario: mockScenarioStr,
  };

  if (googlePayToken) {
    requestBody.google_pay_token = googlePayToken;
  }

  if (payerEmail) {
    requestBody.payer_email = payerEmail;
  }

  if (processorConfirm) {
    if (processorConfirm.processorId) requestBody.processor_id = processorConfirm.processorId;
    if (processorConfirm.customerInfo) requestBody.customer_info = processorConfirm.customerInfo;
    if (processorConfirm.paymentMethodId) requestBody.payment_method_id = processorConfirm.paymentMethodId;
    if (processorConfirm.expectedAmount !== undefined) requestBody.expected_amount = processorConfirm.expectedAmount;
    if (processorConfirm.expectedCurrency) requestBody.expected_currency = processorConfirm.expectedCurrency;
    if (processorConfirm.payerBillingAddress) requestBody.payer_billing_address = processorConfirm.payerBillingAddress;
    if (processorConfirm.payerShippingAddress) {
      requestBody.payer_shipping_address = processorConfirm.payerShippingAddress;
    }
    requestBody.fraud_metadata = toFraudMetadataWire(checkoutRequestId);
  }

  const result = await apiCall<GooglePayConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/google-pay/confirm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return {
      charge_status: "fail",
      error_message: result.error || "Failed to confirm payment",
    };
  }

  return result.data;
}

/**
 * Call /verify endpoint - verifies 3DS completion for Airwallex.
 * Called after user completes 3DS challenge. May return another next_action for retry.
 */
async function callVerifyEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  checkoutRequestId?: string,
): Promise<GooglePayConfirmResponse> {
  const result = await apiCall<GooglePayConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/google-pay/verify`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return {
      charge_status: "fail",
      error_message: result.error || "Failed to verify payment",
    };
  }

  return result.data;
}

/**
 * Convert GooglePayConfirmResponse to GooglePayResult for return to caller.
 */
function toGooglePayResult(response: GooglePayConfirmResponse, secureToken: string): GooglePayResult {
  if (response.charge_status === "success") {
    return {
      data: {
        id: response.transaction_id,
        checkoutAttemptId: response.checkout_attempt_id,
        checkoutSessionId: response.checkout_session_id ?? secureToken,
        state: "checkout_succeeded",
        paymentIntentId: response.payment_intent_id,
        customerId: response.customer_id,
        customerFirstName: response.customer_first_name,
        customerLastName: response.customer_last_name,
        paymentMethodId: response.payment_method_id,
        processorUsed: response.processor_used,
        subscriptionId: response.subscription_id,
        invoiceId: response.invoice_id,
        invoiceNumber: response.invoice_number,
        cardBrand: response.card_brand,
        cardLast4: response.card_last4,
        cardExpMonth: response.card_exp_month,
        cardExpYear: response.card_exp_year,
        errorCode: response.error_code,
        errorMessageForCustomer: response.error_message_for_customer,
        errorMessageForDebug: response.error_message_for_debug,
        nextAction: response.next_action,
      },
    };
  }
  return {
    errors: {
      google_pay:
        response.error_message_for_customer ||
        response.error_message_for_debug ||
        response.error_message ||
        "Payment failed",
    },
  };
}

// =============================================================================
// Stripe Confirm (Simple - No 3DS Loop)
// =============================================================================

/**
 * Confirm Stripe Google Pay - simple flow without 3DS loop.
 * Stripe.js handles 3DS internally during confirmCardSetup().
 */
async function confirmStripeGooglePay(
  apiBaseUrl: string,
  secureToken: string,
  // REQUIRED, and positioned before the optional params on purpose: as a trailing
  // optional it could be silently omitted by a future call site, which would skip
  // merchant validation with no type error and no test failure. Required means
  // TypeScript forces every call site to make a deliberate choice.
  gate: BeforeConfirmOptions,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  payerEmail?: string,
  stripeConfirm?: {
    processorId?: string;
    customerInfo?: GooglePayCustomerInfo;
    paymentMethodId?: string;
    expectedAmount?: number;
    expectedCurrency?: string;
    // Billing address from the Stripe sheet (PAY-1953) — saved on the customer.
    payerBillingAddress?: AddressPayload;
    // Shipping address from the sheet, when asked for — saved on the customer.
    payerShippingAddress?: AddressPayload;
  },
): Promise<GooglePayResult> {
  // Merchant pre-charge validation (PAY-1540). Reached from both Stripe entry
  // points (prepared fast path and the legacy post-tap flow); the shopper has
  // authorized but nothing is charged until callConfirmEndpoint below. The sheet
  // was already completed in runPreparedStripeFlow, so aborting here just stops
  // the charge — there is no sheet left to dismiss.
  const decision = await runBeforeConfirm(gate.beforeConfirm, gate.beforeConfirmTimeoutMs);
  if (!decision.proceed) {
    return { errors: { google_pay: decision.error } };
  }

  const response = await callConfirmEndpoint(
    apiBaseUrl,
    secureToken,
    undefined,
    mockScenarioStr,
    checkoutRequestId,
    payerEmail,
    stripeConfirm,
  );
  return toGooglePayResult(response, secureToken);
}

// =============================================================================
// Airwallex Confirm with 3DS Loop
// =============================================================================

const MAX_USER_ACTIONS = 5;

/**
 * Confirm Airwallex Google Pay with 3DS loop support.
 *
 * Flow:
 * 1. Call /confirm with token → may return next_action (3DS)
 * 2. If next_action: handle 3DS, then call /verify
 * 3. Loop if /verify returns another next_action (3DS retry)
 * 4. Return final result
 *
 * This mirrors the card.ts pattern for consistency.
 */
async function confirmAirwallexGooglePay(
  apiBaseUrl: string,
  secureToken: string,
  googlePayToken: GooglePayEncryptedToken,
  // REQUIRED and positioned before the optional params — see confirmStripeGooglePay.
  gate: BeforeConfirmOptions,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  payerEmail?: string,
  // Present on the prepared fast path (PAY-1538): /start was a pure read, so the
  // customer + checkout attempt are created at /confirm from these fields.
  processorConfirm?: {
    processorId?: string;
    customerInfo?: GooglePayCustomerInfo;
    expectedAmount?: number;
    expectedCurrency?: string;
    payerShippingAddress?: AddressPayload;
  },
): Promise<GooglePayResult> {
  let userActionCount = 0;

  // Merchant pre-charge validation (PAY-1540). Runs once, before the initial
  // confirm — never inside the 3DS loop below, where a charge is already in
  // flight and re-validating could only produce an inconsistent abort.
  const decision = await runBeforeConfirm(gate.beforeConfirm, gate.beforeConfirmTimeoutMs);
  if (!decision.proceed) {
    return { errors: { google_pay: decision.error } };
  }

  // Step 1: Initial confirm with token
  let response = await callConfirmEndpoint(
    apiBaseUrl,
    secureToken,
    googlePayToken,
    mockScenarioStr,
    checkoutRequestId,
    payerEmail,
    processorConfirm,
  );

  // Step 2: Handle 3DS loop (if required)
  while (response.charge_status === "pending" && response.next_action && userActionCount < MAX_USER_ACTIONS) {
    userActionCount++;

    // Handle 3DS challenge
    const actionResult = await handleNextAction(response.next_action);

    // Always call verify endpoint so backend can conclude properly
    // (even if 3DS failed, backend needs to know)
    const verifyResponse = await callVerifyEndpoint(apiBaseUrl, secureToken, checkoutRequestId);

    // Check if another 3DS action is required (retry scenario)
    if (verifyResponse.charge_status === "pending" && verifyResponse.next_action) {
      if (!actionResult.success) {
        console.log("[GooglePay:Airwallex] 3DS failed but retry available, continuing loop...");
      }
      response = verifyResponse;
      continue;
    }

    // No more actions - check final result
    if (!actionResult.success) {
      // 3DS failed and no retry available
      return {
        errors: {
          google_pay: verifyResponse.error_message || actionResult.error || "3DS authentication failed",
        },
      };
    }

    // 3DS succeeded - return verify result
    return toGooglePayResult(verifyResponse, secureToken);
  }

  // Check for max attempts exceeded
  if (userActionCount >= MAX_USER_ACTIONS) {
    return { errors: { google_pay: "Too many authentication attempts. Please try again." } };
  }

  // No 3DS required - return initial confirm result
  return toGooglePayResult(response, secureToken);
}

// =============================================================================
// Main Submit Function
// =============================================================================

const defSubmitPayment = (
  states: PaymentKitStates,
  instanceHooks: {
    getAutoPrepareConfig: () => AutoPrepareConfig | null;
    getPrepared: () => PreparedGooglePayState | null;
    /** Drop the prepared state AND kick a background re-prepare (drives readiness). */
    clearAndRearmPrepared: () => void;
    isSubmitInFlight: () => boolean;
    setSubmitInFlight: (inFlight: boolean) => void;
    /** Whether the sheet should ask for a shipping address right now. */
    getRequestShippingAddress: () => boolean;
  },
) => {
  const submitPayment: TInternalFuncs["submitPayment"] = async (fields, options) => {
    const { apiBaseUrl, secureToken, environment } = states;
    const gpayOptions = options as GooglePaySubmitOptions;
    const autoPrepareConfig = instanceHooks.getAutoPrepareConfig();

    // Validate options
    const validationError = validateOptions(gpayOptions, autoPrepareConfig);
    if (validationError) {
      return { errors: validationError };
    }

    // Re-entrancy guard: a second tap while the sheet or /confirm is outstanding
    // must not open a second sheet / create a second charge.
    if (instanceHooks.isSubmitInFlight()) {
      return { errors: { google_pay: "A Google Pay payment is already in progress." } };
    }
    instanceHooks.setSubmitInFlight(true);

    // A shipping address the shopper typed into the merchant's form rides on
    // customer_info, where the backend prefers it over the sheet's.
    const typedShipping = shippingAddressFromFields(fields as ShippingFields);
    // Sheets built at tap time (Airwallex) skip asking when the shopper already
    // typed one, in `fields` or in customerInfo.shipping_address (same street-line
    // rule). The prepared Stripe sheet was built earlier;
    // setRequestShippingAddress(false) controls it.
    const hasTypedShipping = !!(typedShipping || clean(gpayOptions.customerInfo?.shipping_address?.line1));
    const requestShippingAddress = instanceHooks.getRequestShippingAddress() && !hasTypedShipping;

    // Use autoPrepareConfig as fallback for processorId
    const effectiveOptions: GooglePaySubmitOptions = {
      ...gpayOptions,
      processorId: gpayOptions.processorId || autoPrepareConfig?.processorId || "",
      ...(typedShipping ? { customerInfo: { ...gpayOptions.customerInfo, shipping_address: typedShipping } } : {}),
    };

    try {
      const mockScenarioStr = getMockScenarioStr(gpayOptions.mockScenario);

      // Get or create checkout request ID for correlating all API calls
      const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
      console.log(`[GooglePay] Using checkout_request_id: ${checkoutRequestId}`);

      const prepared = instanceHooks.getPrepared();

      // Fast path: auto-prepare already prepared the Stripe rail. Show the sheet
      // immediately (Stripe requires it open within the user gesture), then confirm —
      // the customer + checkout attempt + SetupIntent are created server-side at /confirm.
      if (prepared?.kind === "stripe") {
        try {
          const stripeResult = await runPreparedStripeFlow(prepared);
          if (!stripeResult.success) {
            return { errors: { google_pay: stripeResult.error } };
          }
          return await confirmStripeGooglePay(
            apiBaseUrl,
            secureToken,
            effectiveOptions,
            prepared.mockScenarioStr,
            prepared.checkoutRequestId,
            stripeResult.payerEmail,
            {
              // The processor the sheet was PREPARED for — the submit-time id can
              // legitimately diverge (e.g. an Apple-Pay-selected processor) and
              // would route the backend into the wrong confirm branch.
              processorId: prepared.processorId,
              customerInfo: effectiveOptions.customerInfo,
              paymentMethodId: stripeResult.paymentMethodId,
              payerBillingAddress: stripeResult.payerBillingAddress,
              payerShippingAddress: stripeResult.payerShippingAddress,
              expectedAmount: prepared.startData.amount,
              expectedCurrency: prepared.startData.currency,
            },
          );
        } finally {
          // The W3C PaymentRequest is one-shot (show() throws on reuse), so the
          // prepared Stripe state cannot survive a sheet showing — even a cancel.
          // Re-arm so readiness stays truthful and the NEXT tap is gesture-safe
          // again instead of silently degrading to the legacy post-tap flow.
          instanceHooks.clearAndRearmPrepared();
        }
      }

      // Fast path (PAY-1538): auto-prepare already fetched the Airwallex sheet config
      // and initialized the adapter. Open the sheet immediately — no network round-trip
      // may sit between the tap and loadPaymentData(), or slow connections outlive the
      // ~5s transient user activation and Chrome rejects the popup (NotAllowedError).
      // The customer + checkout attempt are created server-side at /confirm.
      // Mock scenarios stay on the legacy flow below — the prepared adapter was built
      // without a mock scenario at page load.
      if (prepared?.kind === "airwallex" && !mockScenarioStr) {
        if (effectiveOptions.processorId && effectiveOptions.processorId !== prepared.processorId) {
          // Confirm with the processor the sheet was PREPARED for — a divergent
          // submit-time processorId would route the backend into the wrong confirm
          // branch (e.g. legacy attempt lookup on a session with no attempt).
          console.warn(
            `[GooglePay] submit processorId ${effectiveOptions.processorId} differs from prepared ` +
              `${prepared.processorId} — confirming with the prepared processor (the sheet was built for it).`,
          );
        }
        const sheetResult = await prepared.adapter.showPaymentSheet({ requestShippingAddress });
        if (!sheetResult.success) {
          // Keep the prepared state: loadPaymentData() is reusable (unlike the W3C
          // PaymentRequest), and clearing here would push the common
          // cancel-then-retry tap back onto the gesture-unsafe legacy /start path.
          if ("cancelled" in sheetResult && sheetResult.cancelled) {
            return { errors: { google_pay: "Google Pay cancelled by user" } };
          }
          const errorMessage = "error" in sheetResult ? sheetResult.error : "Unknown error";
          return { errors: { google_pay: errorMessage } };
        }
        const confirmResult = await confirmAirwallexGooglePay(
          apiBaseUrl,
          secureToken,
          sheetResult.token,
          effectiveOptions,
          mockScenarioStr,
          prepared.checkoutRequestId,
          sheetResult.payerEmail,
          {
            processorId: prepared.processorId,
            customerInfo: effectiveOptions.customerInfo,
            expectedAmount: prepared.amount,
            expectedCurrency: prepared.currency,
            payerShippingAddress: sheetResult.payerShippingAddress,
          },
        );
        if (!confirmResult.errors) {
          // Successful charge: this sheet config is spent. Clear + re-arm so a
          // further tap (still-open session) prepares fresh state instead of
          // re-charging from a stale one.
          instanceHooks.clearAndRearmPrepared();
        }
        return confirmResult;
      }

      // Step 1: Start Google Pay flow - backend selects processor.
      // Stripe /start is a pure read (no writes); Airwallex /start still creates the
      // checkout attempt from customer_info/fraud_metadata sent here.
      const startResult = await callStartEndpoint(
        apiBaseUrl,
        secureToken,
        effectiveOptions,
        mockScenarioStr,
        checkoutRequestId,
      );
      if (startResult.error || !startResult.data) {
        return { errors: { google_pay: startResult.error || "Failed to start Google Pay" } };
      }

      const startData = startResult.data;

      // Step 2 & 3: Run processor-specific flow and confirm
      if (startData.processor === "stripe") {
        // Stripe without prepared state (e.g. amount changed mid-flight). /start was a
        // pure read, so build + probe the adapter now — this may fail Stripe's
        // user-gesture requirement, which is why auto-prepare is the primary path.
        if (!startData.stripe_pk) {
          return { errors: { google_pay: "Stripe publishable key not provided" } };
        }
        const { adapter, error: adapterError } = initializeStripeAdapter(
          startData.stripe_pk,
          startData,
          gpayOptions.mockScenario,
          requestShippingAddress,
        );
        if (!adapter) {
          return { errors: { google_pay: adapterError } };
        }
        const available = await adapter.canMakePayment();
        if (!available) {
          return { errors: { google_pay: "Google Pay not available on this device" } };
        }
        const stripeResult = await runPreparedStripeFlow({
          kind: "stripe",
          adapter,
          startData,
          mockScenarioStr,
          checkoutRequestId,
        });
        if (!stripeResult.success) {
          return { errors: { google_pay: stripeResult.error } };
        }
        return await confirmStripeGooglePay(
          apiBaseUrl,
          secureToken,
          effectiveOptions,
          mockScenarioStr,
          checkoutRequestId,
          stripeResult.payerEmail,
          {
            processorId: effectiveOptions.processorId,
            customerInfo: effectiveOptions.customerInfo,
            paymentMethodId: stripeResult.paymentMethodId,
            payerBillingAddress: stripeResult.payerBillingAddress,
            payerShippingAddress: stripeResult.payerShippingAddress,
          },
        );
      }

      if (startData.processor === "airwallex") {
        // Airwallex flow: Uses Google Pay API directly, may need 3DS
        if (requestShippingAddress) {
          console.warn(
            "[GooglePay] requestShippingAddress is not supported on this Airwallex fallback path " +
              "(Google Pay was not prepared); the sheet will not ask for a shipping address.",
          );
        }
        const airwallexResult = await runAirwallexFlow(startData, gpayOptions.mockScenario);
        if (!airwallexResult.success) {
          return { errors: { google_pay: airwallexResult.error } };
        }
        if (!airwallexResult.token) {
          return { errors: { google_pay: "Google Pay token not received" } };
        }
        // Confirm with 3DS loop support
        return await confirmAirwallexGooglePay(
          apiBaseUrl,
          secureToken,
          airwallexResult.token,
          effectiveOptions,
          mockScenarioStr,
          checkoutRequestId,
          airwallexResult.payerEmail,
        );
      }

      return { errors: { google_pay: `Unsupported processor: ${startData.processor}` } };
    } catch (error) {
      return { errors: { google_pay: `Google Pay error: ${error}` } };
    } finally {
      instanceHooks.setSubmitInFlight(false);
    }
  };

  return submitPayment;
};

// =============================================================================
// Payment Method Definition
// =============================================================================

const GooglePayPaymentMethod = definePaymentMethod((paymentKitStates) => {
  const { apiBaseUrl, secureToken, environment } = paymentKitStates;

  // Per-instance auto-prepare state — isolated from other GooglePayPaymentMethod instances.
  let autoPrepareConfig: AutoPrepareConfig | null = null;
  // Per-session Google-Pay processors list, fetched once only when a `processorIds.googlePay` pin
  // needs its family resolved (PAY-1701). Cached across re-prepare / notifyAmountChanged.
  let googlePayProcessorsCache: WalletProcessorInfo[] | null = null;
  // Prepared sheet state (adapter + config) — also per-instance, so two PaymentKit
  // instances on one page cannot clobber each other's prepared sheet.
  let preparedState: PreparedGooglePayState | null = null;
  // Re-entrancy guard for submitPayment (a second tap while the sheet or /confirm
  // is outstanding must not open a second sheet / create a second charge).
  let submitInFlight = false;
  let onGooglePayReadyCallbacks: ((isReady: boolean, detail?: GooglePayReadyDetail) => void)[] = [];
  let isGooglePayReadyState = false;
  // Why the current state is `false` — replayed to late subscribers alongside the state.
  let googlePayReadyDetail: GooglePayReadyDetail | undefined;
  let autoPrepareInProgress = false;
  // Incremented by cleanup() so that in-flight prepares can detect staleness and skip writes.
  let cleanupGeneration = 0;
  // Stores pending retry args when notifyAmountChanged is called while a prepare is in flight.
  let pendingAutoPrepareArgs: {
    apiBaseUrl: string;
    secureToken: string;
    environment: PaymentKitEnvironment;
  } | null = null;
  // Resolvers waiting for a queued retry to complete (for correct notifyAmountChanged await).
  let pendingPrepareResolvers: (() => void)[] = [];
  // The first prepare reuses PaymentKit's shared session fetch; every later prepare reads the
  // session fresh (PAY-2447).
  const readSessionConfig = createWalletSessionConfigReader(paymentKitStates._initialSessionConfig);
  // Whether the sheet asks for a shipping address. Starts from the init option;
  // setRequestShippingAddress() changes it (and re-prepares the Stripe sheet).
  let requestShippingAddress = paymentKitStates.requestShippingAddress === true;

  function instanceSetGooglePayReady(ready: boolean, detail?: GooglePayReadyDetail): void {
    isGooglePayReadyState = ready;
    googlePayReadyDetail = ready ? undefined : detail;
    for (const cb of onGooglePayReadyCallbacks) cb(ready, googlePayReadyDetail);
  }

  function instanceClearPreparedGooglePay(): void {
    preparedState = null;
    instanceSetGooglePayReady(false, { reason: "preparing" });
  }

  // Consumed-prepared-state path (successful confirm, or a one-shot Stripe sheet
  // showing): drop the state and re-prepare in the background so readiness stays
  // truthful and the next tap is gesture-safe again.
  function instanceClearAndRearmPrepared(): void {
    instanceClearPreparedGooglePay();
    instanceAutoPrepare(apiBaseUrl, secureToken, environment).catch((err) =>
      console.error("[GooglePay] Re-prepare after submit failed:", err),
    );
  }

  async function instanceAutoPrepare(apBase: string, sToken: string, env: PaymentKitEnvironment): Promise<void> {
    if (autoPrepareInProgress) {
      // A prepare is already in flight; queue a retry and return a promise that resolves after it.
      pendingAutoPrepareArgs = { apiBaseUrl: apBase, secureToken: sToken, environment: env };
      return new Promise<void>((resolve) => pendingPrepareResolvers.push(resolve));
    }
    autoPrepareInProgress = true;
    // Capture generation so we can detect if cleanup() is called while we await.
    const capturedGeneration = cleanupGeneration;
    instanceSetGooglePayReady(false, { reason: "preparing" });

    try {
      const result = await readSessionConfig<CheckoutSessionConfig>(apiCall, apBase, sToken);

      if (cleanupGeneration !== capturedGeneration) return;

      if (result.error || !result.data) {
        console.warn("[GooglePay] Auto-prepare: failed to fetch session config:", result.error);
        instanceSetGooglePayReady(false, { reason: "session_fetch_failed" });
        return;
      }

      // PAY-1701: a merchant-supplied `processorIds.googlePay` (PaymentKit init option) OVERRIDES
      // the backend scan, so the merchant pins exactly which account both MINTS and CHARGES Google
      // Pay (prepare pins it, confirm reuses it → one id end-to-end).
      const override = paymentKitStates.processorIds?.googlePay;
      let processorId: string;
      let processorType: "stripe" | "airwallex" | undefined;

      if (override) {
        // Resolve the pinned processor's REAL family from the backend's Google-Pay processors list
        // — NOT from the scanned default's type, which would mis-route an Airwallex pin into the
        // Stripe /start and 400. Cache the list per instance; only fetched when a pin is set, so
        // the default path stays zero-cost.
        if (!googlePayProcessorsCache) {
          const list = await fetchWalletProcessors(apiCall, apBase, sToken, "google-pay");
          if (cleanupGeneration !== capturedGeneration) return;
          if (!list) {
            instanceSetGooglePayReady(false, { reason: "processors_fetch_failed" });
            return;
          }
          googlePayProcessorsCache = list;
        }
        const resolvedType = resolveWalletProcessorType(googlePayProcessorsCache, override);
        if (!resolvedType) {
          // Fail closed: the pinned id is absent OR its family is unrecognized. Never assume Stripe.
          console.warn(
            `[GooglePay] Pinned processorIds.googlePay '${override}' is not a usable Google-Pay processor ` +
              "for this checkout session (unknown family or not eligible) — Google Pay stays unavailable " +
              "(not mis-routed).",
          );
          instanceSetGooglePayReady(false, { reason: "no_processor" });
          return;
        }
        processorId = override;
        processorType = resolvedType;
      } else {
        // Unset → prefer the Google-Pay processor, falling back to the shared express processor for
        // backends that predate it (PaymentKit's hosted checkout never sets a pin, so it is
        // unaffected).
        const scanned = result.data.google_pay_processor_id ?? result.data.express_checkout_processor_id;
        if (!scanned) {
          console.log("[GooglePay] Auto-prepare: no Google Pay processor configured");
          instanceSetGooglePayReady(false, { reason: "no_processor" });
          return;
        }
        processorId = scanned;
        const raw = result.data.google_pay_processor_id
          ? result.data.google_pay_processor_type
          : result.data.express_checkout_processor_type;
        processorType = raw === "airwallex" || raw === "stripe" ? raw : undefined;
      }

      autoPrepareConfig = { processorId, processorType };

      // Readiness must reflect the rail the CHARGE runs on. For Stripe that is the W3C
      // PaymentRequest rail (canMakePayment().googlePay) — false on Safari — not Google's
      // browser-agnostic isReadyToPay(), which over-reports there (bug #3274). Airwallex
      // stays on isReadyToPay() because that IS its charge rail.
      const prepareResult = await prepareGooglePay(
        apBase,
        sToken,
        { processorId, processorType, requestShippingAddress },
        env,
      );

      // cleanup() ran while we awaited — discard this run's result entirely so a
      // dead instance/session cannot leave a consumable prepared state behind.
      if (cleanupGeneration !== capturedGeneration) return;

      // notifyAmountChanged() arrived mid-prepare: this result reflects the OLD
      // amount. Don't arm it and don't flap ready true→false — the queued retry
      // in the finally below re-prepares with the new amount.
      if (pendingAutoPrepareArgs) return;

      preparedState = prepareResult.prepared ?? null;

      const ready = prepareResult.success && prepareResult.googlePay === true;
      instanceSetGooglePayReady(ready, ready ? undefined : { reason: prepareResult.reason ?? "prepare_error" });
    } catch (err) {
      if (cleanupGeneration === capturedGeneration) {
        console.error("[GooglePay] Auto-prepare failed:", err);
        instanceSetGooglePayReady(false, { reason: "prepare_error" });
      }
    } finally {
      autoPrepareInProgress = false;
      const stale = cleanupGeneration !== capturedGeneration;
      const pending = pendingAutoPrepareArgs;
      const resolvers = pendingPrepareResolvers.splice(0);
      if (!stale && pending) {
        pendingAutoPrepareArgs = null;
        // notifyAmountChanged() arrived while this prepare was in flight — the state
        // this run just cached reflects the OLD amount. Clear it synchronously so no
        // tap can consume it during the retry's round-trips.
        preparedState = null;
        // Run the retry and resolve all callers that were waiting for it.
        instanceAutoPrepare(pending.apiBaseUrl, pending.secureToken, pending.environment).then(() => {
          for (const r of resolvers) r();
        });
      } else {
        pendingAutoPrepareArgs = null;
        for (const r of resolvers) r();
      }
    }
  }

  // Start auto-prepare immediately so the button is ready before first user interaction.
  instanceAutoPrepare(apiBaseUrl, secureToken, environment).catch((err) =>
    console.error("[GooglePay] Unhandled init error:", err),
  );

  return {
    name: "google_pay",
    externalFuncs: {
      /**
       * Register a callback to be notified when Google Pay becomes ready (or stops being ready).
       * Fires immediately with the current state, then on every state change.
       * Multiple registrations are all notified — none are silently overwritten.
       *
       * When `isReady` is false, `detail.reason` explains why (e.g. `"no_processor"`,
       * `"rail_unavailable"` — browser/wallet cannot pay on the charge rail). `detail` is
       * omitted when ready.
       *
       * @example
       * paymentKit.google_pay.onGooglePayReady((isReady, detail) => {
       *   googlePayButton.disabled = !isReady;
       *   if (!isReady) console.log("Google Pay unavailable:", detail?.reason);
       * });
       */
      onGooglePayReady: (callback: (isReady: boolean, detail?: GooglePayReadyDetail) => void): void => {
        onGooglePayReadyCallbacks.push(callback);
        // Fire immediately with current state so caller doesn't miss a completed prepare.
        callback(isGooglePayReadyState, googlePayReadyDetail);
      },

      /**
       * Notify the SDK that the payment amount has changed (e.g. coupon applied).
       * The SDK will disable Google Pay, re-prepare with the new amount, then re-enable.
       * The returned promise resolves when re-prepare is fully complete.
       *
       * @example
       * await paymentKit.google_pay.notifyAmountChanged();
       */
      notifyAmountChanged: (): Promise<void> => {
        instanceClearPreparedGooglePay();
        autoPrepareConfig = null;
        return instanceAutoPrepare(apiBaseUrl, secureToken, environment);
      },

      /**
       * Turn the sheet's shipping-address request on or off. Use it when your own form
       * has the shipping address: stop asking the sheet, and pass the address in
       * `fields` on `submit` instead. The initial value is the `requestShippingAddress`
       * init option.
       *
       * A Stripe sheet is built before the tap, so a change re-prepares Google Pay; it is
       * not ready (`onGooglePayReady(false)`) until the returned promise resolves. A call
       * that does not change the value does nothing.
       *
       * @example
       * // Ask Google Pay for the address only while the shopper has not typed one.
       * await paymentKit.google_pay.setRequestShippingAddress(!form.hasShippingAddress());
       */
      setRequestShippingAddress: (required: boolean): Promise<void> => {
        // Coerce like the init option: only `true` turns it on (a plain-JS "false" must not).
        const next = required === true;
        if (next === requestShippingAddress) return Promise.resolve();
        requestShippingAddress = next;
        instanceClearPreparedGooglePay();
        autoPrepareConfig = null;
        return instanceAutoPrepare(apiBaseUrl, secureToken, environment);
      },
    },
    internalFuncs: {
      submitPayment: defSubmitPayment(paymentKitStates, {
        getAutoPrepareConfig: () => autoPrepareConfig,
        getPrepared: () => preparedState,
        clearAndRearmPrepared: instanceClearAndRearmPrepared,
        isSubmitInFlight: () => submitInFlight,
        setSubmitInFlight: (inFlight: boolean) => {
          submitInFlight = inFlight;
        },
        getRequestShippingAddress: () => requestShippingAddress,
      }),
      cleanup: () => {
        cleanupGeneration++;
        onGooglePayReadyCallbacks = [];
        isGooglePayReadyState = false;
        googlePayReadyDetail = undefined;
        autoPrepareConfig = null;
        googlePayProcessorsCache = null;
        pendingAutoPrepareArgs = null;
        pendingPrepareResolvers = [];
        autoPrepareInProgress = false;
        preparedState = null;
        submitInFlight = false;
      },
    },
  };
});

export { GooglePayMockScenario };

export default GooglePayPaymentMethod;
